// utils/imageFallback.js - Utility for consistent image fallback handling

// Primary placeholder - more reliable SVG data URI that never fails to load
export const DEFAULT_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkFGQUZBIi8+CjxyZWN0IHg9IjkwIiB5PSI2MCIgd2lkdGg9IjEyMCIgaGVpZ2h0PSI4MCIgZmlsbD0iI0UwRTBFMCIgcng9IjQiLz4KPGNpcmNsZSBjeD0iMTIwIiBjeT0iODUiIHI9IjgiIGZpbGw9IiNGQUZBRkEiLz4KPHBhdGggZD0iTTkwIDEyMEwxMjAgOTVMMTY1IDExMEwyMTAgMTI1VjE0MEg5MFYxMjBaIiBmaWxsPSIjQ0NDQ0NDIi8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTY1IiBmb250LWZhbWlseT0ic3lzdGVtLXVpIiBmb250LXNpemU9IjEyIiBmaWxsPSIjNzc3Nzc3IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5JbWFnZSBkZSBwcm9kdWl0PC90ZXh0Pgo8L3N2Zz4K';

// Fallback to file-based placeholder as secondary option
export const FILE_PLACEHOLDER = '/images/placeholder.png';

// Ultra-minimal backup placeholder for worst-case scenarios
export const BACKUP_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkFGQUZBIi8+CjxwYXRoIGQ9Ik0xMjUgNzVIMTc1VjEyNUgxMjVWNzVaIiBmaWxsPSIjRDBEMEQwIi8+CjxwYXRoIGQ9Ik0xNDAgOTBIMTYwVjExMEgxNDBWOTBaIiBmaWxsPSIjRkFGQUZBIi8+CjxwYXRoIGQ9Ik0xMjUgMTI1SDEzNy41TDE1MCA5MEwxNjIuNSAxMjVIMTc1VjE0MEgxMjVWMTI1WiIgZmlsbD0iI0QwRDBEMCIvPgo8dGV4dCB4PSIxNTAiIHk9IjE2NSIgZm9udC1mYW1pbHk9InN5c3RlbS11aSIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzc3Nzc3NyIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgaW5kaXNwb25pYmxlPC90ZXh0Pgo8L3N2Zz4K';

// Create a better placeholder with dynamic sizing and more reliable rendering
export const createPlaceholderSVG = (width = 300, height = 200, text = 'Image non disponible') => {
  // Ensure minimum dimensions for readability
  const minWidth = Math.max(width, 120);
  const minHeight = Math.max(height, 80);
  
  // Calculate responsive sizes
  const rectX = minWidth * 0.25;
  const rectY = minHeight * 0.25;
  const rectWidth = minWidth * 0.5;
  const rectHeight = minHeight * 0.4;
  const circleR = Math.min(minWidth, minHeight) * 0.04;
  const fontSize = Math.max(Math.min(minWidth, minHeight) * 0.08, 10);
  
  const svg = `<svg width="${minWidth}" height="${minHeight}" viewBox="0 0 ${minWidth} ${minHeight}" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="${minWidth}" height="${minHeight}" fill="#FAFAFA"/>
    <rect x="${rectX}" y="${rectY}" width="${rectWidth}" height="${rectHeight}" fill="#E0E0E0" rx="6"/>
    <circle cx="${rectX + rectWidth * 0.25}" cy="${rectY + rectHeight * 0.3}" r="${circleR}" fill="#FAFAFA"/>
    <path d="M${rectX} ${rectY + rectHeight * 0.7}L${rectX + rectWidth * 0.3} ${rectY + rectHeight * 0.45}L${rectX + rectWidth * 0.7} ${rectY + rectHeight * 0.55}L${rectX + rectWidth} ${rectY + rectHeight * 0.7}V${rectY + rectHeight}H${rectX}V${rectY + rectHeight * 0.7}Z" fill="#CCCCCC"/>
    <text x="${minWidth / 2}" y="${minHeight * 0.85}" font-family="system-ui,-apple-system,sans-serif" font-size="${fontSize}" fill="#777777" text-anchor="middle">${text}</text>
  </svg>`;
  
  try {
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  } catch (error) {
    // Fallback if btoa fails - return a simple URL-encoded SVG
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
};

/**
 * Get the appropriate image source with fallback to your custom placeholder
 * @param {string|null|undefined} src - The original image source
 * @param {string} fallback - Custom fallback (optional)
 * @param {boolean} useProxy - Force use of image proxy (optional)
 * @returns {string} - The image source to use
 */
export function getImageSrc(src, fallback = DEFAULT_PLACEHOLDER, useProxy = false) {
  // If no source provided, use reliable SVG fallback
  if (!src || src === 'undefined' || src === 'null') return fallback;
  
  // If it's a problematic external service, use fallback
  if (src.includes('via.placeholder.com') || src.includes('placeholder.com')) {
    return fallback;
  }
  
  // If it starts with /uploads, create full URL
  if (src.startsWith('/uploads/')) {
    const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'https://us-central1-fenparet-website.cloudfunctions.net/api';
    return `${baseUrl}${src}`;
  }
  
  // If it's already a full HTTP URL, return it directly
  // Cloudinary URLs are public and CORS-enabled, no proxy needed
  if (src.startsWith('http')) {
    return src;
  }
  
  // Return the original source
  return src;
}

/**
 * Get proxied image URL for direct use (ensures full URL format)
 * @param {string|null|undefined} src - The original image source 
 * @param {string} fallback - Custom fallback (optional)
 * @returns {string} - The proxied image URL or fallback
 */
export function getProxiedImageUrl(src, fallback = DEFAULT_PLACEHOLDER) {
  if (!src) return fallback;
  
  // If it's a relative path, convert to absolute first
  if (src.startsWith('/uploads/')) {
    const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'https://us-central1-fenparet-website.cloudfunctions.net/api';
    src = `${baseUrl}${src}`;
  }

  // Return full URL directly - Cloudinary doesn't need proxying
  if (src.startsWith('http')) {
    return src;
  }

  return fallback;
}

/**
 * Handle image error by providing consistent fallback
 * @param {Event} error - Image error event
 * @param {Function} setImgSrc - State setter function
 */
export function handleImageError(error, setImgSrc) {
  console.warn('Image failed to load:', error.target?.src);
  setImgSrc(DEFAULT_PLACEHOLDER);
}